import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  Button,
  CircularProgress,
  Divider,
} from '@mui/material';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import HistoryIcon from '@mui/icons-material/History';
import {
  salaryService,
  HrPendingSummary,
  HrActivityItem,
} from '../services/salary.service';

const INK = '#0A0A0A';
const MUTED = '#737373';
const HAIRLINE = '#E5E5E5';
const BG = '#FAFAFA';

const monthLabel = (yyyymm: string): string => {
  const [y, m] = yyyymm.split('-');
  const idx = Math.max(0, Math.min(11, parseInt(m || '1', 10) - 1));
  const names = ['January', 'February', 'March', 'April', 'May', 'June',
                 'July', 'August', 'September', 'October', 'November', 'December'];
  return `${names[idx]} ${y}`;
};

const friendlyTimestamp = (iso: string | undefined): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

interface KpiCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint: string;
  status?: 'ok' | 'warn' | 'attention';
  ctaLabel: string;
  onCta: () => void;
}

function KpiCard({ icon, label, value, hint, status = 'ok', ctaLabel, onCta }: KpiCardProps) {
  const statusBg = status === 'warn' ? '#FFFBEB' : status === 'attention' ? '#FEF2F2' : BG;
  const statusFg = status === 'warn' ? '#92400E' : status === 'attention' ? '#991B1B' : MUTED;
  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 1,
        border: `1px solid ${HAIRLINE}`,
        bgcolor: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        minHeight: 175,
      }}
    >
      <Stack direction="row" alignItems="center" gap={1}>
        <Box sx={{ color: MUTED, display: 'flex' }}>{icon}</Box>
        <Typography sx={{ fontSize: 12, fontWeight: 500, color: MUTED, letterSpacing: 0.2, textTransform: 'uppercase' }}>
          {label}
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: 28, fontWeight: 600, color: INK, letterSpacing: -0.5, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
      <Box
        component="span"
        sx={{
          alignSelf: 'flex-start',
          fontSize: 11,
          px: 1,
          py: 0.25,
          borderRadius: 0.75,
          bgcolor: statusBg,
          color: statusFg,
        }}
      >
        {hint}
      </Box>
      <Box sx={{ flex: 1 }} />
      <Button
        onClick={onCta}
        variant="outlined"
        size="small"
        sx={{
          alignSelf: 'flex-start',
          borderColor: HAIRLINE,
          color: INK,
          textTransform: 'none',
          fontWeight: 500,
          '&:hover': { borderColor: INK, bgcolor: 'transparent' },
        }}
      >
        {ctaLabel}
      </Button>
    </Box>
  );
}

export default function HrHome() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<HrPendingSummary | null>(null);
  const [activity, setActivity] = useState<HrActivityItem[]>([]);
  const [loading, setLoading] = useState(true);

  const [endpointMissing, setEndpointMissing] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      // Run independently so one failure doesn't blank the page
      const sRes = await salaryService.getHrPendingSummary().catch((err) => {
        if (mounted && (err?.status === 404 || /not.*found/i.test(err?.message || ''))) {
          setEndpointMissing(true);
        }
        return null;
      });
      const aRes = await salaryService.getHrRecentActivity(10).catch(() => null);
      if (!mounted) return;
      if (sRes) setSummary(sRes.summary);
      if (aRes) setActivity(aRes.activity);
      setLoading(false);
    })();
    return () => { mounted = false; };
  }, []);

  const todayLabel = useMemo(() => {
    if (!summary) return '';
    const d = new Date(summary.today);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  }, [summary]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <CircularProgress size={32} sx={{ color: INK }} />
      </Box>
    );
  }

  if (!summary) {
    return (
      <Box sx={{ p: 3, maxWidth: 1280, mx: 'auto' }}>
        <Typography sx={{ fontSize: 24, fontWeight: 600, letterSpacing: -0.4, color: INK }}>
          HR Home
        </Typography>
        <Typography sx={{ fontSize: 12, color: MUTED, mb: 3 }}>
          Daily attendance, monthly payroll, and the team at a glance.
        </Typography>
        <Box sx={{ p: 4, border: `1px solid ${HAIRLINE}`, borderRadius: 1, bgcolor: '#FFFFFF', textAlign: 'center' }}>
          <Typography sx={{ color: INK, fontWeight: 500, mb: 1 }}>
            {endpointMissing ? 'Backend not yet updated' : 'Could not load HR summary'}
          </Typography>
          <Typography sx={{ color: MUTED, fontSize: 13, mb: 2 }}>
            {endpointMissing
              ? 'The HR dashboard endpoints aren\'t deployed yet. Use the navigation below.'
              : 'Use the navigation below to continue.'}
          </Typography>
          <Stack direction="row" gap={1} justifyContent="center">
            <Button variant="outlined" onClick={() => navigate('/employees')} sx={{ borderColor: HAIRLINE, color: INK, textTransform: 'none' }}>
              Employees
            </Button>
            <Button variant="outlined" onClick={() => navigate('/attendance')} sx={{ borderColor: HAIRLINE, color: INK, textTransform: 'none' }}>
              Attendance
            </Button>
            <Button variant="contained" onClick={() => navigate('/payroll')} sx={{ bgcolor: INK, '&:hover': { bgcolor: '#262626' }, textTransform: 'none' }}>
              Payroll
            </Button>
          </Stack>
        </Box>
      </Box>
    );
  }

  const attendanceStatus = summary.unmarkedAttendanceToday === 0 ? 'ok' : 'warn';
  const attendanceHint = summary.unmarkedAttendanceToday === 0
    ? `All marked for ${todayLabel}`
    : `${summary.unmarkedAttendanceToday} unmarked for ${todayLabel}`;

  const payrollNeedsAction = summary.draftSlipsThisMonth + summary.generatedSlipsThisMonth + summary.pendingApprovalSlipsThisMonth > 0;
  const payrollStatus = payrollNeedsAction ? 'warn' : 'ok';
  const payrollValue = `${summary.generatedSlipsThisMonth + summary.pendingApprovalSlipsThisMonth} · ${summary.sentSlipsThisMonth} · ${summary.paidSlipsThisMonth}`;
  const payrollHint = payrollNeedsAction
    ? `${summary.generatedSlipsThisMonth + summary.pendingApprovalSlipsThisMonth} slip${summary.generatedSlipsThisMonth + summary.pendingApprovalSlipsThisMonth === 1 ? '' : 's'} awaiting review`
    : `${monthLabel(summary.currentMonth)} payroll complete`;

  return (
    <Box sx={{ p: 3, maxWidth: 1280, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: 24, fontWeight: 600, letterSpacing: -0.4, color: INK }}>
          HR Home
        </Typography>
        <Typography sx={{ fontSize: 12, color: MUTED }}>
          Daily attendance, monthly payroll, and the team at a glance.
        </Typography>
      </Box>

      {/* KPI cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 3 }}>
        <KpiCard
          icon={<EventNoteOutlinedIcon sx={{ fontSize: 18 }} />}
          label="Today's attendance"
          value={`${summary.markedAttendanceToday} / ${summary.headcount}`}
          hint={attendanceHint}
          status={attendanceStatus}
          ctaLabel="Mark today"
          onCta={() => navigate('/attendance')}
        />
        <KpiCard
          icon={<PaidOutlinedIcon sx={{ fontSize: 18 }} />}
          label={`${monthLabel(summary.currentMonth)} payroll`}
          value={payrollValue}
          hint={payrollHint}
          status={payrollStatus}
          ctaLabel="Continue payroll"
          onCta={() => navigate('/payroll')}
        />
        <KpiCard
          icon={<GroupsOutlinedIcon sx={{ fontSize: 18 }} />}
          label="Headcount"
          value={String(summary.headcount)}
          hint={summary.joinersThisMonth > 0
            ? `${summary.joinersThisMonth} joined${summary.latestJoinerName ? ` · latest: ${summary.latestJoinerName}` : ''}`
            : 'No new joiners this month'}
          status="ok"
          ctaLabel="Add employee"
          onCta={() => navigate('/employees?add=1')}
        />
      </Box>

      {/* Pipeline summary for current month */}
      <Box
        sx={{
          mb: 3,
          p: 2.5,
          border: `1px solid ${HAIRLINE}`,
          borderRadius: 1,
          bgcolor: '#FFFFFF',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.5 }}>
          <Typography sx={{ fontWeight: 600, color: INK }}>
            {monthLabel(summary.currentMonth)} pipeline
          </Typography>
          <Typography sx={{ fontSize: 11, color: MUTED }}>
            Generate → Review → Send → Pay
          </Typography>
        </Stack>
        <Stack direction="row" divider={<Divider orientation="vertical" flexItem />} gap={2}>
          {[
            { label: 'Generated', value: summary.generatedSlipsThisMonth + summary.draftSlipsThisMonth },
            { label: 'Awaiting review', value: summary.pendingApprovalSlipsThisMonth + summary.generatedSlipsThisMonth },
            { label: 'Sent', value: summary.sentSlipsThisMonth },
            { label: 'Paid', value: summary.paidSlipsThisMonth },
          ].map((stat) => (
            <Box key={stat.label} sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 11, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                {stat.label}
              </Typography>
              <Typography sx={{ fontSize: 20, fontWeight: 600, color: INK, fontVariantNumeric: 'tabular-nums', mt: 0.25 }}>
                {stat.value}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* Recent activity */}
      <Box sx={{ p: 2.5, border: `1px solid ${HAIRLINE}`, borderRadius: 1, bgcolor: '#FFFFFF' }}>
        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
          <HistoryIcon sx={{ fontSize: 18, color: MUTED }} />
          <Typography sx={{ fontWeight: 600, color: INK }}>Recent activity</Typography>
        </Stack>
        {activity.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: MUTED }}>
            Nothing yet — activity will appear here as you onboard, mark attendance, and run payroll.
          </Typography>
        ) : (
          <Stack gap={1}>
            {activity.map((item, idx) => (
              <Stack
                key={idx}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ py: 0.75, borderBottom: idx < activity.length - 1 ? `1px solid ${HAIRLINE}` : 'none' }}
              >
                <Typography sx={{ fontSize: 13, color: INK }}>{item.message}</Typography>
                <Typography sx={{ fontSize: 11, color: MUTED }}>{friendlyTimestamp(item.timestamp)}</Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Box>

      {/* Quick actions */}
      <Box sx={{ mt: 3 }}>
        <Stack direction="row" gap={1}>
          <Button
            variant="contained"
            startIcon={<PersonAddAlt1OutlinedIcon />}
            onClick={() => navigate('/employees?add=1')}
            sx={{ bgcolor: INK, '&:hover': { bgcolor: '#262626' }, textTransform: 'none' }}
          >
            Add Employee
          </Button>
          <Button
            variant="outlined"
            startIcon={<EventNoteOutlinedIcon />}
            onClick={() => navigate('/attendance')}
            sx={{ borderColor: HAIRLINE, color: INK, textTransform: 'none' }}
          >
            Mark Today's Attendance
          </Button>
          <Button
            variant="outlined"
            startIcon={<PaidOutlinedIcon />}
            onClick={() => navigate('/payroll')}
            sx={{ borderColor: HAIRLINE, color: INK, textTransform: 'none' }}
          >
            Open Payroll
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
